/**
 * Отправка документа врачам и клиникам: прайс-лист, карта цветов.
 *
 * Одно окно на оба случая: список получателей с поиском и галочками, тема,
 * текст письма и одно вложение. Отличаются только вложение и то, кому вообще
 * можно писать — карту цветов клиникам не шлют: цвет выбирает тот, кто лечит.
 *
 * Адреса берутся из карточек: у кого почты нет, тот в список не попадает
 * вовсе — отмечать получателя, которому нельзя написать, незачем.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { mailAddressSchema, splitMailAddresses } from '@otc/shared';
import { Check, Paperclip, Search, Send, Upload, X } from 'lucide-react';
import { api } from '../api/client.js';
import { Button, Modal } from './ui.js';

export interface SendTarget {
  id: string;
  name: string;
  email: string;
  /** Подпись под именем: клиника врача или юрлицо. */
  note?: string;
}

export function SendDocDialog({
  title,
  doctors,
  legals,
  subject: subject0,
  body: body0,
  attachment,
  hint,
  onAttachmentReplace,
  onClose,
  onSend,
  onDone,
}: {
  title: string;
  doctors: SendTarget[];
  /** Юрлица клиник. Пусто — письмо только врачам. */
  legals?: SendTarget[];
  subject: string;
  body: string;
  /** Что уйдёт во вложении: имя файла или пояснение. Пусто — вложения нет. */
  attachment?: string;
  /** Строка под текстом письма: история рассылок, пояснение — что нужно. */
  hint?: ReactNode;
  /** Заменить файл вложения. Есть только у карты цветов. */
  onAttachmentReplace?: (file: File) => Promise<void>;
  onClose: () => void;
  onSend: (кому: { doctorIds: string[]; legalIds: string[]; emails: string[]; subject: string; body: string }) => Promise<{
    sent: number;
    results: { name: string; email: string; ok: boolean; error: string }[];
  }>;
  onDone: (text: string) => void;
}) {
  const [manualEmails, setManualEmails] = useState('');
  const emails = splitMailAddresses(manualEmails);
  const [subject, setSubject] = useState(subject0);
  const [body, setBody] = useState(body0);
  const [поиск, setПоиск] = useState('');
  /** Какую половину показываем: врачей или клиники. */
  const [группа, setГруппа] = useState<'doctors' | 'legals'>('doctors');
  const [выбраны, setВыбраны] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [итог, setИтог] = useState<{ name: string; email: string; ok: boolean; error: string }[] | null>(
    null,
  );

  /* Без почты писать некуда — таких в списке нет вовсе. */
  const врачи = useMemo(() => doctors.filter((д) => д.email.trim()), [doctors]);
  const юрлица = useMemo(() => (legals ?? []).filter((л) => л.email.trim()), [legals]);

  const строка = поиск.trim().toLowerCase();
  const подходит = (ц: SendTarget) =>
    !строка ||
    ц.name.toLowerCase().includes(строка) ||
    (ц.note ?? '').toLowerCase().includes(строка) ||
    ц.email.toLowerCase().includes(строка);

  const видимыеВрачи = врачи.filter(подходит);
  const видимыеЮрлица = юрлица.filter(подходит);

  const переключить = (id: string) =>
    setВыбраны((было) => {
      const стало = new Set(было);
      if (стало.has(id)) стало.delete(id);
      else стало.add(id);
      return стало;
    });

  const отметитьВсех = (кого: SendTarget[], включить: boolean) =>
    setВыбраны((было) => {
      const стало = new Set(было);
      for (const ц of кого) {
        if (включить) стало.add(ц.id);
        else стало.delete(ц.id);
      }
      return стало;
    });

  const отправить = async () => {
    const doctorIds = врачи.filter((д) => выбраны.has(д.id)).map((д) => д.id);
    const legalIds = юрлица.filter((л) => выбраны.has(л.id)).map((л) => л.id);
    if (doctorIds.length + legalIds.length + emails.length === 0) {
      setError('Никто не выбран');
      return;
    }
    if (emails.length > 300 || emails.some(email => !mailAddressSchema.safeParse(email).success)) { setError('Проверьте введённые адреса почты (не более 300)'); return; }
    setBusy(true);
    setError(null);
    try {
      const ответ = await onSend({ doctorIds, legalIds, emails, subject, body });
      setИтог(ответ.results);
      const не = ответ.results.filter((р) => !р.ok).length;
      onDone(не === 0 ? `Отправлено: ${ответ.sent}` : `Отправлено: ${ответ.sent}, не удалось: ${не}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось отправить');
    } finally {
      setBusy(false);
    }
  };

  const заменить = async (файл: File | undefined) => {
    if (!файл || !onAttachmentReplace) return;
    setBusy(true);
    setError(null);
    try {
      await onAttachmentReplace(файл);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось загрузить файл');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={title}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <span className="text-xs text-ink2">
            Получателей: {выбраны.size + emails.length}
          </span>
          <span className="ml-auto flex items-center gap-2">
            <Button onClick={() => void отправить()} disabled={busy || выбраны.size + emails.length === 0 || !subject.trim() || !body.trim()}>
              <Send size={14} /> {busy ? 'Отправляем…' : 'Отправить'}
            </Button>
            <Button variant="ghost" onClick={onClose}>
              Закрыть
            </Button>
          </span>
        </>
      }
    >
      {error && (
        <p className="mb-3 text-sm" style={{ color: 'var(--otc-status-critical)' }}>
          {error}
        </p>
      )}

      {/* Вложение — первым: человек должен видеть, ЧТО уходит. */}
      {attachment && (
      <div
        className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border px-3 py-2 text-sm"
        style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}
      >
        <Paperclip size={15} className="text-ink2" />
        <span className="text-ink">{attachment}</span>
        {onAttachmentReplace && (
          <label className="ml-auto inline-flex cursor-pointer items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs text-ink2"
            style={{ borderColor: 'var(--otc-line)' }}
          >
            <Upload size={13} /> Заменить файл
            <input
              type="file"
              accept="application/pdf"
              className="hidden"
              onChange={(e) => void заменить(e.target.files?.[0])}
            />
          </label>
        )}
      </div>
      )}

      <label className="mb-2 block text-xs text-ink2">
        Тема письма
        <input className="otc-input mt-1" value={subject} onChange={(e) => setSubject(e.target.value)} />
      </label>

      <label className="mb-3 block text-xs text-ink2">
        Текст письма
        <textarea
          className="otc-input mt-1"
          rows={4}
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
      </label>

      <label className="mb-3 block text-xs text-ink2">Добавить почту вручную
        <textarea aria-label="Добавить почту вручную" className="otc-input mt-1 w-full" rows={2} value={manualEmails} onChange={e=>setManualEmails(e.target.value)} placeholder="email@example.ru — несколько адресов через запятую" disabled={busy}/>
        <span className="mt-1 block">Можно отправить только на введённые адреса или добавить их к выбранным ниже.</span>
      </label>
      {hint}

      {/*
        ВРАЧИ И КЛИНИКИ — раздельными переключателями.

        Одним списком с заголовками посередине выбирать было неудобно: чтобы
        добраться до клиник, приходилось прокручивать шесть десятков врачей,
        а отметив кого-то, уже не понять, сколько отмечено в другой половине.
        Выбор при переключении сохраняется — счётчик внизу общий.
      */}
      {юрлица.length > 0 && (
        <div
          className="mb-2 flex w-fit gap-1 rounded-lg p-1"
          style={{ background: 'var(--otc-surface)', border: '1px solid var(--otc-line)' }}
        >
          {([
            ['doctors', `Врачи · ${врачи.filter((д) => выбраны.has(д.id)).length}/${врачи.length}`],
            ['legals', `Клиники · ${юрлица.filter((л) => выбраны.has(л.id)).length}/${юрлица.length}`],
          ] as const).map(([ключ, подпись]) => (
            <button
              key={ключ}
              onClick={() => setГруппа(ключ)}
              className="rounded-md px-3.5 py-1.5 text-sm transition-colors"
              style={{
                background: группа === ключ ? 'var(--otc-accent-dark)' : 'var(--otc-surface)',
                border: `1px solid ${группа === ключ ? 'var(--otc-accent-dark)' : 'var(--otc-line)'}`,
                color: группа === ключ ? '#fff' : 'var(--otc-ink)',
              }}
            >
              {подпись}
            </button>
          ))}
        </div>
      )}

      <div className="mb-2 flex flex-wrap items-center gap-2">
        {/* Лупа стоит ВНУТРИ поля и по центру его высоты: раньше она налезала
            на подсказку и выглядела съехавшей. */}
        <span className="relative inline-flex items-center">
          <Search size={14} className="pointer-events-none absolute left-2.5 text-ink2" />
          <input
            className="otc-input w-56"
            /*
              Отступ под лупу — ИНЛАЙНОМ, а не классом Tailwind.

              У `.otc-input` padding задан сокращённой записью, и она стоит
              в стилях после утилит, поэтому `pl-8` не срабатывал: значок
              налезал на подсказку «Поиск по имени или почте».
            */
            style={{ paddingLeft: '2rem' }}
            placeholder="Поиск по имени или почте"
            value={поиск}
            onChange={(e) => setПоиск(e.target.value)}
          />
        </span>
        <button
          className="rounded-lg border px-2.5 py-1.5 text-xs text-ink transition-colors hover:bg-black/5"
          style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}
          onClick={() => отметитьВсех(группа === 'doctors' ? видимыеВрачи : видимыеЮрлица, true)}
        >
          Отметить всех
        </button>
        <button
          className="rounded-lg border px-2.5 py-1.5 text-xs text-ink transition-colors hover:bg-black/5"
          style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}
          onClick={() => отметитьВсех(группа === 'doctors' ? видимыеВрачи : видимыеЮрлица, false)}
        >
          Снять отметки
        </button>
      </div>

      {/* Список получателей — НА БЕЛОМ: это таблица, а не подложка окна. */}
      <div
        className="max-h-[46vh] overflow-y-auto rounded-lg border"
        style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}
      >
        {(группа === 'doctors' ? видимыеВрачи : видимыеЮрлица).map((ц) => (
          <Строка key={ц.id} цель={ц} выбран={выбраны.has(ц.id)} onToggle={() => переключить(ц.id)} />
        ))}

        {(группа === 'doctors' ? видимыеВрачи : видимыеЮрлица).length === 0 && (
          <p className="px-3 py-6 text-center text-sm text-ink2">Никого не нашлось</p>
        )}
      </div>

      {/* Итог рассылки: по каждому — ушло или нет и почему. */}
      {итог && (
        <div className="mt-3 rounded-lg border p-2.5" style={{ borderColor: 'var(--otc-line)' }}>
          {итог.map((р, i) => (
            <p key={i} className="flex items-center gap-2 py-0.5 text-xs">
              {р.ok ? (
                <Check size={13} style={{ color: 'var(--otc-status-done)' }} />
              ) : (
                <X size={13} style={{ color: 'var(--otc-status-critical)' }} />
              )}
              <span className="text-ink">{р.name}</span>
              <span className="text-ink2">{р.ok ? р.email : р.error}</span>
            </p>
          ))}
        </div>
      )}
    </Modal>
  );
}

function Строка({
  цель,
  выбран,
  onToggle,
}: {
  цель: SendTarget;
  выбран: boolean;
  onToggle: () => void;
}) {
  return (
    <label
      className="flex cursor-pointer items-center gap-2.5 border-t px-3 py-2 text-sm"
      style={{ borderColor: 'var(--otc-line)' }}
    >
      <input type="checkbox" checked={выбран} onChange={onToggle} />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-ink">{цель.name}</span>
        {цель.note && <span className="block truncate text-xs text-ink2">{цель.note}</span>}
      </span>
      <span className="truncate text-xs text-ink2">{цель.email}</span>
    </label>
  );
}

/** Отправка письма: общий вызов для обоих окон. */
export async function отправитьДокумент(
  адрес: string,
  тело: Record<string, unknown>,
): Promise<{ sent: number; results: { name: string; email: string; ok: boolean; error: string }[] }> {
  return api.post(адрес, тело);
}
